import { onDocumentReady } from '@theme/utilities';

const IMAGE_REVEAL_SELECTOR = [
  '.product-media__image',
  '.resource-card__image',
  '.resource-card__collection-image',
  '.deferred-media__poster-image',
  '.collection-image__featured-image',
  '.slideshow-controls__thumbnail img',
  '.dialog-thumbnails-list__thumbnail img',
].join(', ');

/**
 * Mark the image as fully ready for display.
 * @param {HTMLImageElement} image
 * @param {string} signature
 */
function markLoaded(image, signature) {
  if (image.dataset.imageRevealSignature !== signature) return;
  image.setAttribute('data-image-loaded', 'true');
  image.removeAttribute('data-image-error');
}

/**
 * Mark the image as failed so normal browser fallback can take over.
 * @param {HTMLImageElement} image
 * @param {string} signature
 */
function markError(image, signature) {
  if (image.dataset.imageRevealSignature !== signature) return;
  image.setAttribute('data-image-error', 'true');
  image.removeAttribute('data-image-loaded');
}

/**
 * Wait until the image is decoded before revealing it when possible.
 * @param {HTMLImageElement} image
 * @param {string} signature
 */
function revealWhenReady(image, signature) {
  if (typeof image.decode === 'function') {
    image
      .decode()
      .catch(() => null)
      .finally(() => markLoaded(image, signature));
    return;
  }

  markLoaded(image, signature);
}

/**
 * Track a matching image and reveal it when it has decoded.
 * @param {HTMLImageElement} image
 */
function observeImage(image) {
  const src = image.currentSrc || image.getAttribute('src') || image.getAttribute('data-src') || '';
  const srcset = image.getAttribute('srcset') || '';
  const signature = `${src}::${srcset}`;

  if (!signature.trim()) return;

  image.setAttribute('data-image-reveal', 'true');
  image.dataset.imageRevealSignature = signature;
  image.removeAttribute('data-image-loaded');
  image.removeAttribute('data-image-error');

  if (image.complete) {
    if (image.naturalWidth > 0) {
      revealWhenReady(image, signature);
    } else {
      markError(image, signature);
    }
    return;
  }

  image.addEventListener('load', () => revealWhenReady(image, signature), { once: true });
  image.addEventListener('error', () => markError(image, signature), { once: true });
}

/**
 * Process a root node and any matching descendant images.
 * @param {ParentNode | Node} root
 */
function scanImages(root) {
  if (root instanceof HTMLImageElement && root.matches(IMAGE_REVEAL_SELECTOR)) {
    observeImage(root);
    return;
  }

  if (!(root instanceof Element) && !(root instanceof Document)) return;

  if (root instanceof Element && root.matches(IMAGE_REVEAL_SELECTOR)) {
    observeImage(/** @type {HTMLImageElement} */ (root));
  }

  root.querySelectorAll?.(IMAGE_REVEAL_SELECTOR).forEach((image) => observeImage(/** @type {HTMLImageElement} */ (image)));
}

onDocumentReady(() => {
  scanImages(document);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (
        mutation.type === 'attributes' &&
        mutation.target instanceof HTMLImageElement &&
        mutation.target.matches(IMAGE_REVEAL_SELECTOR)
      ) {
        observeImage(mutation.target);
        continue;
      }

      mutation.addedNodes.forEach((node) => scanImages(node));
    }
  });

  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['src', 'srcset'],
  });
});
